<template>
  <!-- "Your front door" (mystic-page 2026-10-29, OUTLINE decisions 2 and 5;
       API.md §2): the canonical address with Copy link, a real QR (the
       portrait in the middle; PNG and SVG), and the counts. "Brought back" is
       a count only, never money. Then "Your daily drop": the horoscope link. -->
  <div class="fd-wrap">
    <div class="studio-card fd" data-testid="front-door">
      <h3 class="fd-title">Your front door</h3>
      <p class="fd-sub">Send people here: your bio, a post, a printed card. Anyone can open it, signed out, and start a reading with you.</p>
      <div class="fd-grid">
        <div class="fd-main">
          <div class="fd-addr">
            <span class="fd-addr-text" data-testid="front-door-address">{{ address }}</span>
            <button type="button" class="fd-copy" data-testid="front-door-copy" @click="copy">{{ copied ? 'Copied' : 'Copy link' }}</button>
          </div>
          <p class="fd-help">Paste it in your Instagram, TikTok or LinkedIn bio. Someone who arrives through it and starts their first reading has you as their home Mystic.</p>
          <div v-if="counts.length" class="fd-counts">
            <div v-for="c in counts" :key="c.key" class="fd-count" data-testid="front-door-count">
              <span class="fd-n">{{ c.n.toLocaleString() }}</span>
              <span class="fd-l">{{ c.label }}</span>
            </div>
          </div>
        </div>
        <div class="fd-qr-col">
          <div class="fd-qr" data-testid="front-door-qr" role="img" :aria-label="`QR code for ${address}`" v-html="svg"></div>
          <div class="fd-dl">
            <a href="#" data-testid="front-door-png" @click.prevent="downloadPng">Download PNG</a>
            <a :href="svgHref" :download="`${sub}-front-door.svg`" data-testid="front-door-svg">SVG</a>
          </div>
        </div>
      </div>
    </div>

    <div class="studio-card fd fd-drop">
      <h3 class="fd-title">Your daily drop</h3>
      <p class="fd-sub">Written each morning while you’re active. The same drop shows on your front door and in the Community’s “Today from the Mystics”.</p>
      <a class="fd-link" :href="horoscopeUrl" target="_blank" rel="noopener" data-testid="front-door-horoscope">
        <span class="fd-link-ico" aria-hidden="true">✦</span>
        <span class="fd-link-body"><span class="fd-link-title">Horoscopes &amp; Mystic Drops</span><span class="fd-link-sub">{{ horoscopeUrl.replace(/^https?:\/\//, '') }}</span></span>
        <span class="fd-link-arrow" aria-hidden="true">↗</span>
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getStudioFrontDoor } from '@/api-service'
import { copyText } from '@/utils/care-link'
import { qrSvg, qrPng } from '@/utils/qr-code'

const props = defineProps({
  sub: { type: String, default: '' },
  portrait: { type: String, default: '' },
})

const PUBLIC_DOMAIN = 'mysticaida.ai'
const door = ref(null)
const link = computed(() => (door.value && door.value.link) || `https://${props.sub}.${PUBLIC_DOMAIN}`)
const address = computed(() => link.value.replace(/^https?:\/\//, '').replace(/\/$/, ''))
const qrText = computed(() => (door.value && door.value.qr_text) || `${link.value.replace(/\/$/, '')}/?via=qr`)
const horoscopeUrl = computed(() => (door.value && door.value.horoscope_url) || `https://${PUBLIC_DOMAIN}/horoscope/${props.sub}`)
const svg = computed(() => qrSvg(qrText.value, { logo: props.portrait }))
const svgHref = computed(() => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg.value)}`)

const counts = computed(() => {
  const c = door.value && door.value.counts
  if (!c) return []
  const n = (x) => Number((x && x.month) || 0)
  return [
    { key: 'new', n: n(c.new_seekers), label: 'new seekers through your page this month' },
    { key: 'back', n: n(c.brought_back), label: 'seekers you brought back this month' },
    { key: 'visits', n: n(c.visits), label: 'visits to your page this month' },
  ]
})

const copied = ref(false)
async function copy () {
  if (await copyText(link.value)) {
    copied.value = true
    setTimeout(() => { copied.value = false }, 2200)
  }
}
async function downloadPng () {
  try {
    const url = await qrPng(qrText.value, { logo: props.portrait })
    const a = document.createElement('a')
    a.href = url; a.download = `${props.sub}-front-door.png`
    document.body.appendChild(a); a.click(); a.remove()
  } catch (e) { /* the SVG is still there */ }
}

onMounted(async () => {
  door.value = await getStudioFrontDoor()
})
</script>

<style scoped>
.fd-wrap { display: flex; flex-direction: column; gap: 20px; margin-bottom: 20px; }
.fd-title { margin: 0 0 4px; font: 500 22px/1.2 'Cormorant Garamond', Georgia, serif; color: #fff4dc; }
.fd-sub { margin: 0 0 16px; font-size: 13px; line-height: 1.45; color: rgba(214, 198, 245, 0.78); }
.fd-grid { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 22px; align-items: start; }
.fd-addr { display: flex; align-items: center; gap: 10px; padding: 6px 6px 6px 14px; border-radius: 12px; border: 1px solid rgba(240, 217, 163, 0.45); background: rgba(10, 4, 22, 0.55); }
.fd-addr-text { flex: 1; min-width: 0; font-size: 16px; font-weight: 700; color: #f6e4b4; overflow-wrap: anywhere; }
.fd-copy { flex: 0 0 auto; min-height: 36px; padding: 6px 16px; border: 0; border-radius: 9px; cursor: pointer; font: 700 14px inherit; font-family: inherit; color: #2a1a05; background: #f6e4b4; }
.fd-copy:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 2px; }
.fd-help { margin: 12px 0 14px; font-size: 13px; line-height: 1.5; color: rgba(214, 198, 245, 0.78); }
.fd-counts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.fd-count { display: flex; flex-direction: column; gap: 4px; padding: 12px 12px 14px; border-radius: 12px; background: rgba(255, 255, 255, 0.05); }
.fd-n { font: 600 22px/1 'Cormorant Garamond', Georgia, serif; color: #f6e4b4; }
.fd-l { font-size: 12.5px; line-height: 1.35; color: rgba(214, 198, 245, 0.8); }
.fd-qr-col { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.fd-qr { width: 200px; height: 200px; padding: 0; border-radius: 16px; overflow: hidden; background: #fbf6ea; }
.fd-qr :deep(svg) { display: block; width: 100%; height: 100%; }
.fd-dl { display: flex; gap: 10px; font-size: 13px; font-weight: 700; }
.fd-dl a { color: #f6e4b4; text-decoration: underline; text-underline-offset: 3px; }
.fd-link { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 12px; background: rgba(255, 255, 255, 0.04); text-decoration: none; color: inherit; }
.fd-link:hover { background: rgba(255, 255, 255, 0.07); }
.fd-link-ico { color: #f6e4b4; }
.fd-link-body { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.fd-link-title { font-size: 14px; font-weight: 700; color: #fff; }
.fd-link-sub { font-size: 12px; color: rgba(214, 198, 245, 0.75); overflow-wrap: anywhere; }
.fd-link-arrow { color: #f6e4b4; }
@media (max-width: 699px) {
  .fd-grid { grid-template-columns: minmax(0, 1fr); }
  .fd-counts { grid-template-columns: minmax(0, 1fr); }
  .fd-qr-col { margin-top: 4px; }
  .fd-qr { width: min(240px, 70vw); height: min(240px, 70vw); }
}
</style>
